import React, {useEffect, useState} from 'react';
import FormModal from '../../../Presentation/components/common/FormModal';
import FormField from '../../../Presentation/components/common/FormField';
import ChecklistItemsEditor from './ChecklistItemsEditor';
import {
  Checklist,
  ChecklistItem,
  ChecklistPayload,
  emptyChecklistItem
} from '../models/checklist.model';

// Phase 1: Channel + Assignment are not surfaced in the UI. The DB columns are
// kept (defaulted to 'Other') so Phase 2 can reintroduce admin-managed channels /
// assignment types + per-role routing without a migration.
const DEFAULT_CHANNEL = 'Other';
const DEFAULT_ASSIGNMENT = 'Other';

interface ChecklistFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: Checklist | null;
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (payload: ChecklistPayload) => void;
}

interface FormState {
  name: string;
  description: string;
  effective_date: string;
  items: ChecklistItem[];
}

const blankState = (): FormState => ({
  name: '',
  description: '',
  effective_date: '',
  items: [emptyChecklistItem(0)]
});

const ChecklistFormModal: React.FC<ChecklistFormModalProps> = ({open, mode, initial, isLoading, onClose, onSubmit}) => {
  const [form, setForm] = useState<FormState>(blankState());
  const [errors, setErrors] = useState<{name?: string; items?: string}>({});

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setForm({
        name: initial.name,
        description: initial.description ?? '',
        effective_date: initial.effective_date ? initial.effective_date.slice(0, 10) : '',
        items: (initial.items && initial.items.length > 0
          ? [...initial.items].sort((a, b) => a.display_order - b.display_order)
          : [emptyChecklistItem(0)])
      });
    } else {
      setForm(blankState());
    }
    setErrors({});
  }, [open, mode, initial]);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({...prev, ...patch}));

  const validate = (): boolean => {
    const next: {name?: string; items?: string} = {};
    if (!form.name.trim()) next.name = 'اسم القائمة مطلوب';
    if (form.items.length === 0) next.items = 'أضف بنداً واحداً على الأقل';
    else if (form.items.some((it) => !it.item_name.trim())) next.items = 'كل بند يجب أن يحمل اسماً';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload: ChecklistPayload = {
      name: form.name.trim(),
      description: form.description.trim() ? form.description.trim() : null,
      channel: DEFAULT_CHANNEL,
      assignment_type: DEFAULT_ASSIGNMENT,
      effective_date: form.effective_date || null,
      items: form.items.map((it, i) => ({
        item_name: it.item_name.trim(),
        item_type: it.item_type,
        mandatory: it.mandatory,
        display_order: i,
        active: it.active
      }))
    };
    onSubmit(payload);
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={mode === 'create' ? 'إنشاء قائمة تحقق' : 'تعديل قائمة التحقق'}
      submitLabel={mode === 'create' ? 'إنشاء' : 'حفظ التغييرات'}
      isLoading={isLoading}
      width="max-w-3xl"
    >
      <FormField
        type="text"
        label="اسم القائمة"
        placeholder="مثال: قائمة تحقق المشرف"
        value={form.name}
        onChange={(e) => set({name: e.target.value})}
        error={errors.name}
        autoFocus
      />

      <FormField
        type="textarea"
        label="الوصف (اختياري)"
        placeholder="وصف موجز للقائمة"
        value={form.description}
        onChange={(e) => set({description: e.target.value})}
        rows={2}
      />

      <div className="w-full">
        <label className="block mb-2 font-bold text-gray-700">تاريخ السريان (اختياري)</label>
        <input
          type="date"
          value={form.effective_date}
          onChange={(e) => set({effective_date: e.target.value})}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      <ChecklistItemsEditor
        items={form.items}
        onChange={(items) => set({items})}
        disabled={isLoading}
        error={errors.items}
      />
    </FormModal>
  );
};

export default ChecklistFormModal;
